import { useCallback, useEffect, useMemo, useState } from 'react';
import { Check, X, ExternalLink } from 'lucide-react';
import { apiPersons, apiTimeOffs } from '../api/client.js';
import Modal from '../components/Modal.jsx';
import { timeOffDurationLabel, gescomRequestUrl } from '../components/HalfDayPicker.jsx';

/**
 * Sprint 4 — Vue admin validation des congés.
 *
 * Tableau toutes les demandes filtrables par status. Approuver / Refuser via
 * /api/timeoffs/:id/approve|reject avec note optionnelle.
 */

const STATUS_PILL = {
  PENDING: { bg: 'var(--warning)', label: 'En attente' },
  APPROVED: { bg: 'var(--success)', label: 'Approuvé' },
  REJECTED: { bg: 'var(--danger)', label: 'Refusé' },
};

const STATUS_FILTERS = [
  { value: 'PENDING', label: 'En attente' },
  { value: 'APPROVED', label: 'Approuvés' },
  { value: 'REJECTED', label: 'Refusés' },
  { value: 'ALL', label: 'Tous' },
];

const inputStyle = {
  width: '100%',
  padding: '8px 10px',
  background: 'var(--glass)',
  color: 'var(--text)',
  border: '1px solid var(--glass-border)',
  borderRadius: 'var(--radius-sm)',
  fontSize: 14,
};

const primaryBtn = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  background: 'var(--accent)',
  color: '#fff',
  border: 'none',
  borderRadius: 'var(--radius-pill)',
  padding: '8px 16px',
  fontSize: 13,
  fontWeight: 500,
  cursor: 'pointer',
};

const secondaryBtn = {
  background: 'transparent',
  color: 'var(--text)',
  border: '1px solid var(--glass-border)',
  borderRadius: 'var(--radius-sm)',
  padding: '6px 14px',
  fontSize: 13,
  cursor: 'pointer',
};

const pillBtn = {
  borderRadius: 999,
  border: '1px solid var(--glass-border)',
  padding: '4px 12px',
  fontSize: 12,
  fontWeight: 500,
  cursor: 'pointer',
};

function toDateInput(iso) {
  if (!iso) return '';
  return new Date(iso).toISOString().slice(0, 10);
}

export default function TimeOffValidationView() {
  const [items, setItems] = useState([]);
  const [persons, setPersons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('PENDING');

  // Modale approve/reject
  const [actionModal, setActionModal] = useState(null); // { type: 'approve'|'reject', timeOff }
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [actionError, setActionError] = useState(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [{ items: itemsAll }, { items: personsAll }] = await Promise.all([
        apiTimeOffs.list('all'),
        apiPersons.list('all'),
      ]);
      setItems(itemsAll);
      setPersons(personsAll);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  const personById = useMemo(() => {
    const m = new Map();
    for (const p of persons) m.set(p.id, p);
    return m;
  }, [persons]);

  const filtered = useMemo(() => {
    const filt = statusFilter === 'ALL' ? items : items.filter((t) => t.status === statusFilter);
    return [...filt].sort((a, b) => (b.startDate ?? '').localeCompare(a.startDate ?? ''));
  }, [items, statusFilter]);

  const openAction = (type, timeOff) => {
    setActionModal({ type, timeOff });
    setNote('');
    setActionError(null);
  };

  const onSubmitAction = async () => {
    if (!actionModal) return;
    const { type, timeOff } = actionModal;
    if (type === 'reject' && !note.trim()) {
      setActionError('Une note de refus est obligatoire');
      return;
    }
    setSubmitting(true);
    setActionError(null);
    try {
      if (type === 'approve') {
        await apiTimeOffs.approve(timeOff.id, note.trim() || null);
      } else {
        await apiTimeOffs.reject(timeOff.id, note.trim());
      }
      setActionModal(null);
      await reload();
    } catch (e) {
      setActionError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div style={{ padding: 32, color: 'var(--text-muted)' }}>Chargement…</div>;

  return (
    <div style={{ padding: '24px 32px', maxWidth: 1400, margin: '0 auto' }}>
      <div
        style={{
          position: 'sticky',
          top: 0,
          background: 'var(--bg)',
          paddingBottom: 12,
          zIndex: 5,
          display: 'flex',
          alignItems: 'center',
          gap: 16,
          marginBottom: 16,
        }}
      >
        <h1 style={{ margin: 0, fontSize: 22, color: 'var(--text)' }}>Validation des congés</h1>
        <div style={{ display: 'inline-flex', gap: 6 }}>
          {STATUS_FILTERS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => setStatusFilter(opt.value)}
              style={{
                ...pillBtn,
                background: statusFilter === opt.value ? 'var(--primary)' : 'var(--glass)',
                color: statusFilter === opt.value ? '#fff' : 'var(--text)',
              }}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {error ? <div style={{ color: 'var(--danger)', marginBottom: 12 }}>{error}</div> : null}

      <div
        style={{
          background: 'var(--card-bg)',
          border: '1px solid var(--glass-border)',
          borderRadius: 'var(--radius-md)',
          overflow: 'auto',
          boxShadow: 'var(--shadow-card)',
        }}
      >
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
          <thead>
            <tr style={{ background: 'var(--glass)', textAlign: 'left' }}>
              <th style={{ padding: '10px 12px', color: 'var(--text-muted)', fontWeight: 500 }}>Personne(s)</th>
              <th style={{ padding: '10px 12px', color: 'var(--text-muted)', fontWeight: 500 }}>Type</th>
              <th style={{ padding: '10px 12px', color: 'var(--text-muted)', fontWeight: 500 }}>Période</th>
              <th style={{ padding: '10px 12px', color: 'var(--text-muted)', fontWeight: 500 }}>h/jour</th>
              <th style={{ padding: '10px 12px', color: 'var(--text-muted)', fontWeight: 500 }}>Notes demandeur</th>
              <th style={{ padding: '10px 12px', color: 'var(--text-muted)', fontWeight: 500 }}>Statut</th>
              <th style={{ padding: '10px 12px', color: 'var(--text-muted)', fontWeight: 500, width: 150 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={7} style={{ padding: 20, color: 'var(--text-muted)' }}>
                  Aucune demande pour ce filtre.
                </td>
              </tr>
            ) : (
              filtered.map((t) => {
                const pill = STATUS_PILL[t.status] ?? STATUS_PILL.PENDING;
                const personNames = (t.persons ?? [])
                  .map((tp) => personById.get(tp.personId))
                  .filter(Boolean)
                  .map((p) => `${p.firstName} ${p.lastName}`)
                  .join(', ');
                return (
                  <tr key={t.id} style={{ borderTop: '1px solid var(--glass-border)' }}>
                    <td style={{ padding: '10px 12px', color: 'var(--text)' }}>
                      {personNames || '—'}
                    </td>
                    <td style={{ padding: '10px 12px', color: 'var(--text-muted)' }}>
                      {t.timeOffType?.name ?? '—'}
                    </td>
                    <td style={{ padding: '10px 12px', color: 'var(--text-muted)' }}>
                      {toDateInput(t.startDate)} → {toDateInput(t.endDate)}
                      {gescomRequestUrl(t.gescomAbsenceId) ? (
                        <a
                          href={gescomRequestUrl(t.gescomAbsenceId)}
                          target="_blank"
                          rel="noopener noreferrer"
                          title={`Voir la demande ${t.gescomAbsenceId} dans Gescom`}
                          style={{ marginLeft: 6, color: 'var(--accent)', verticalAlign: 'middle' }}
                        >
                          <ExternalLink size={13} />
                        </a>
                      ) : null}
                    </td>
                    <td style={{ padding: '10px 12px', color: 'var(--text-muted)' }}>
                      {timeOffDurationLabel(t)}
                    </td>
                    <td style={{ padding: '10px 12px', color: 'var(--text-muted)', maxWidth: 260 }}>
                      {t.notes ?? '—'}
                    </td>
                    <td style={{ padding: '10px 12px' }}>
                      <span
                        style={{
                          display: 'inline-block',
                          padding: '2px 10px',
                          borderRadius: 999,
                          fontSize: 11,
                          fontWeight: 500,
                          background: pill.bg,
                          color: '#fff',
                        }}
                      >
                        {pill.label}
                      </span>
                      {t.status !== 'PENDING' && t.statusCreator ? (
                        <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 4 }}>
                          par {t.statusCreator.name ?? t.statusCreator.email}
                        </div>
                      ) : null}
                      {t.statusNote ? (
                        <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 4, fontStyle: 'italic' }}>
                          « {t.statusNote} »
                        </div>
                      ) : null}
                    </td>
                    <td style={{ padding: '10px 12px' }}>
                      {t.status === 'PENDING' && !t.archivedAt ? (
                        <div style={{ display: 'inline-flex', gap: 6 }}>
                          <button
                            type="button"
                            onClick={() => openAction('approve', t)}
                            title="Approuver"
                            style={{
                              background: 'transparent',
                              border: '1px solid var(--glass-border)',
                              borderRadius: 'var(--radius-sm)',
                              padding: 6,
                              color: 'var(--success)',
                              cursor: 'pointer',
                            }}
                          >
                            <Check size={14} />
                          </button>
                          <button
                            type="button"
                            onClick={() => openAction('reject', t)}
                            title="Refuser"
                            style={{
                              background: 'transparent',
                              border: '1px solid var(--glass-border)',
                              borderRadius: 'var(--radius-sm)',
                              padding: 6,
                              color: 'var(--danger)',
                              cursor: 'pointer',
                            }}
                          >
                            <X size={14} />
                          </button>
                        </div>
                      ) : (
                        <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>—</span>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <Modal
        open={Boolean(actionModal)}
        onClose={() => setActionModal(null)}
        title={
          actionModal?.type === 'approve'
            ? 'Approuver la demande'
            : 'Refuser la demande'
        }
        footer={
          <>
            <button type="button" onClick={() => setActionModal(null)} style={secondaryBtn}>
              Annuler
            </button>
            <button
              type="button"
              onClick={onSubmitAction}
              disabled={submitting || (actionModal?.type === 'reject' && !note.trim())}
              style={{
                ...primaryBtn,
                background: actionModal?.type === 'reject' ? 'var(--danger)' : 'var(--success)',
                opacity: submitting ? 0.6 : 1,
              }}
            >
              {actionModal?.type === 'reject' ? 'Refuser' : 'Approuver'}
            </button>
          </>
        }
      >
        <div style={{ display: 'grid', gap: 12 }}>
          <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
            {actionModal?.type === 'reject'
              ? 'Indiquez la raison du refus (obligatoire).'
              : 'Ajouter une note (optionnel).'}
          </div>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={4}
            maxLength={2000}
            placeholder={actionModal?.type === 'reject' ? 'Motif du refus…' : 'Note (optionnel)…'}
            style={{ ...inputStyle, fontFamily: 'inherit', resize: 'vertical' }}
          />
          {actionError ? (
            <div style={{ color: 'var(--danger)', fontSize: 13 }}>{actionError}</div>
          ) : null}
        </div>
      </Modal>
    </div>
  );
}
